import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Search, Download, ChevronLeft, ChevronRight, X, FileText, Phone, User, Pill, FileSpreadsheet } from "lucide-react";
import { Button } from "../components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { API, formatCurrency, formatDoctor } from "./pharmacyConstants";

export default function ScheduleReport({ dateFrom, dateTo }) {
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(false);
  const [scheduleOptions, setScheduleOptions] = useState([]);
  const [filters, setFilters] = useState({ schedule: "", mrn: "", phone: "", medicine: "" });
  const [exporting, setExporting] = useState(null); // null | "csv" | "excel" | "pdf"

  useEffect(() => {
    (async () => {
      try {
        const { data: d } = await axios.get(`${API}/pharmacy/schedule/filters`);
        setScheduleOptions(d.schedules || []);
      } catch {}
    })();
  }, []);

  const buildParams = useCallback(() => {
    const params = {};
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    if (filters.schedule) params.schedule = filters.schedule;
    if (filters.mrn) params.mrn = filters.mrn;
    if (filters.phone) params.phone = filters.phone;
    if (filters.medicine) params.medicine = filters.medicine;
    return params;
  }, [dateFrom, dateTo, filters]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = { ...buildParams(), page, page_size: 50 };
      const { data: res } = await axios.get(`${API}/pharmacy/schedule/report`, { params });
      setData(res.data || []);
      setTotal(res.total || 0);
      setTotalPages(res.total_pages || 0);
    } catch (e) {
      toast.error("Failed to load schedule report");
    }
    setLoading(false);
  }, [page, buildParams]);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { setPage(1); }, [filters, dateFrom, dateTo]);

  const handleExport = async (format) => {
    setExporting(format);
    try {
      const endpoint = format === "excel" ? "export-excel" : format === "pdf" ? "export-pdf" : "export";
      const r = await axios.get(`${API}/pharmacy/schedule/${endpoint}`, { params: buildParams(), responseType: "blob" });
      const ext = format === "excel" ? "xlsx" : format === "pdf" ? "pdf" : "csv";
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a"); a.href = url; a.download = `schedule_drug_report.${ext}`; a.click();
      window.URL.revokeObjectURL(url);
      toast.success(`${format.toUpperCase()} exported`);
    } catch { toast.error(`${format} export failed`); }
    setExporting(null);
  };

  const clearFilters = () => { setFilters({ schedule: "", mrn: "", phone: "", medicine: "" }); setPage(1); };
  const hasFilters = filters.schedule || filters.mrn || filters.phone || filters.medicine;

  return (
    <div className="space-y-4" data-testid="schedule-report">
      {/* Filters */}
      <div className="bg-white border border-slate-200 rounded-xl px-5 py-4">
        <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
          <div>
            <label className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-1.5 block">Drug Schedule</label>
            <select value={filters.schedule} onChange={e => setFilters(f => ({ ...f, schedule: e.target.value }))}
              className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg bg-slate-50 focus:bg-white"
              data-testid="schedule-filter-type">
              <option value="">All Schedules (H/H1)</option>
              {scheduleOptions.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-1.5 block">MRN</label>
            <div className="relative">
              <User className="absolute left-3 top-2.5 w-3.5 h-3.5 text-slate-400" />
              <input value={filters.mrn} onChange={e => setFilters(f => ({ ...f, mrn: e.target.value }))}
                placeholder="Search MRN..."
                className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-lg bg-slate-50 focus:bg-white"
                data-testid="schedule-filter-mrn" />
            </div>
          </div>
          <div>
            <label className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-1.5 block">Phone Number</label>
            <div className="relative">
              <Phone className="absolute left-3 top-2.5 w-3.5 h-3.5 text-slate-400" />
              <input value={filters.phone} onChange={e => setFilters(f => ({ ...f, phone: e.target.value }))}
                placeholder="Search phone..."
                className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-lg bg-slate-50 focus:bg-white"
                data-testid="schedule-filter-phone" />
            </div>
          </div>
          <div>
            <label className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-1.5 block">Medicine Name</label>
            <div className="relative">
              <Pill className="absolute left-3 top-2.5 w-3.5 h-3.5 text-slate-400" />
              <input value={filters.medicine} onChange={e => setFilters(f => ({ ...f, medicine: e.target.value }))}
                placeholder="Search medicine..."
                className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-lg bg-slate-50 focus:bg-white"
                data-testid="schedule-filter-medicine" />
            </div>
          </div>
          <div className="flex items-end gap-2 col-span-2 md:col-span-2">
            {hasFilters && (
              <Button variant="ghost" size="sm" onClick={clearFilters} className="text-xs text-slate-500" data-testid="schedule-clear-filters">
                <X className="w-3 h-3 mr-1" /> Clear
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={() => handleExport("csv")} disabled={!!exporting} data-testid="schedule-export-csv">
              <Download className="w-3.5 h-3.5 mr-1" /> {exporting === "csv" ? "..." : "CSV"}
            </Button>
            <Button variant="outline" size="sm" onClick={() => handleExport("excel")} disabled={!!exporting}
              className="border-emerald-300 text-emerald-700 hover:bg-emerald-50" data-testid="schedule-export-excel">
              <FileSpreadsheet className="w-3.5 h-3.5 mr-1" /> {exporting === "excel" ? "..." : "Excel"}
            </Button>
            <Button variant="outline" size="sm" onClick={() => handleExport("pdf")} disabled={!!exporting}
              className="border-red-300 text-red-700 hover:bg-red-50" data-testid="schedule-export-pdf">
              <FileText className="w-3.5 h-3.5 mr-1" /> {exporting === "pdf" ? "..." : "PDF"}
            </Button>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="px-4 py-2.5 border-b border-slate-100 flex items-center justify-between">
          <span className="text-xs text-slate-500 flex items-center gap-1.5">
            <FileText className="w-3.5 h-3.5 text-violet-500" />
            {total} entries | Page {page} of {totalPages || 1}
          </span>
          <div className="flex items-center gap-1">
            <Button variant="ghost" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)} data-testid="schedule-prev">
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <Button variant="ghost" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} data-testid="schedule-next">
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="text-[10px] font-bold uppercase whitespace-nowrap">Order ID</TableHead>
                <TableHead className="text-[10px] font-bold uppercase whitespace-nowrap">Date</TableHead>
                <TableHead className="text-[10px] font-bold uppercase whitespace-nowrap">Type</TableHead>
                <TableHead className="text-[10px] font-bold uppercase whitespace-nowrap">MRN</TableHead>
                <TableHead className="text-[10px] font-bold uppercase whitespace-nowrap">Patient</TableHead>
                <TableHead className="text-[10px] font-bold uppercase whitespace-nowrap">Age</TableHead>
                <TableHead className="text-[10px] font-bold uppercase whitespace-nowrap">Gender</TableHead>
                <TableHead className="text-[10px] font-bold uppercase whitespace-nowrap">Phone</TableHead>
                <TableHead className="text-[10px] font-bold uppercase whitespace-nowrap">Ward</TableHead>
                <TableHead className="text-[10px] font-bold uppercase whitespace-nowrap">Medicine</TableHead>
                <TableHead className="text-[10px] font-bold uppercase whitespace-nowrap">Schedule</TableHead>
                <TableHead className="text-[10px] font-bold uppercase whitespace-nowrap">HSN</TableHead>
                <TableHead className="text-[10px] font-bold uppercase whitespace-nowrap text-center">QTY</TableHead>
                <TableHead className="text-[10px] font-bold uppercase whitespace-nowrap text-right">MRP</TableHead>
                <TableHead className="text-[10px] font-bold uppercase whitespace-nowrap">Doctor</TableHead>
                <TableHead className="text-[10px] font-bold uppercase whitespace-nowrap">Batch</TableHead>
                <TableHead className="text-[10px] font-bold uppercase whitespace-nowrap">Expiry</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && <TableRow><TableCell colSpan={17} className="text-center py-12 text-sm text-slate-400">Loading...</TableCell></TableRow>}
              {!loading && data.length === 0 && <TableRow><TableCell colSpan={17} className="text-center py-12 text-sm text-slate-400">No schedule drug entries found</TableCell></TableRow>}
              {!loading && data.map((r, i) => (
                <TableRow key={`${r.orderId}-${r.medicineName}-${i}`} className="hover:bg-slate-50 transition-colors" data-testid={`schedule-row-${i}`}>
                  <TableCell className="text-xs font-mono font-semibold text-slate-700">{r.orderId}</TableCell>
                  <TableCell className="text-xs whitespace-nowrap">{r.dateOfInvoice}</TableCell>
                  <TableCell className="text-xs">{r.visitType || "-"}</TableCell>
                  <TableCell className="text-xs font-mono">{r.mrn}</TableCell>
                  <TableCell className="text-xs max-w-[130px] truncate">{r.patientName}</TableCell>
                  <TableCell className="text-xs text-center">{r.patientAge || "-"}</TableCell>
                  <TableCell className="text-xs">{r.patientGender || "-"}</TableCell>
                  <TableCell className="text-xs font-mono">{r.phoneNumber || "-"}</TableCell>
                  <TableCell className="text-xs">{r.ward || "-"}</TableCell>
                  <TableCell className="text-xs font-medium text-slate-800 max-w-[180px] truncate">{r.medicineName}</TableCell>
                  <TableCell className="text-xs">
                    <span className={`inline-flex px-2 py-0.5 rounded text-[10px] font-semibold ${
                      r.drugClassification?.toUpperCase().includes("H1") ? "bg-red-100 text-red-700" :
                      r.drugClassification?.toUpperCase().includes("H") ? "bg-orange-100 text-orange-700" :
                      r.drugClassification?.toUpperCase().includes("X") ? "bg-purple-100 text-purple-700" :
                      "bg-slate-100 text-slate-600"
                    }`}>{r.drugClassification}</span>
                  </TableCell>
                  <TableCell className="text-xs font-mono text-slate-500">{r.hsnCode}</TableCell>
                  <TableCell className="text-xs text-center font-medium">{r.quantity}</TableCell>
                  <TableCell className="text-xs text-right tabular-nums">{formatCurrency(r.mrp)}</TableCell>
                  <TableCell className="text-xs max-w-[120px] truncate">{formatDoctor(r.doctorName)}</TableCell>
                  <TableCell className="text-xs font-mono">{r.batchNumber}</TableCell>
                  <TableCell className="text-xs whitespace-nowrap">{r.expiryDate}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        {totalPages > 1 && (
          <div className="px-4 py-2.5 border-t border-slate-100 flex items-center justify-between">
            <span className="text-xs text-slate-500">Showing {(page - 1) * 50 + 1}-{Math.min(page * 50, total)} of {total}</span>
            <div className="flex items-center gap-1">
              <Button variant="ghost" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}><ChevronLeft className="w-4 h-4" /></Button>
              <span className="text-xs font-semibold text-slate-600 px-2">{page} / {totalPages}</span>
              <Button variant="ghost" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}><ChevronRight className="w-4 h-4" /></Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
